import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { useTranslate } from '@gameday/utils/hooks/useTranslate';

import setAutoPlayTrackingObj from './utils/setAutoPlayTrackingObj';
import HighlightCardList from './components/HighlightCardList';
import HighlightVideoPlayer from './components/HighlightVideoPlayer';
import HighlightsPlaylistSkeleton from './components/HighlightsPlaylistSkeleton';
import {
    HighlightPlaylistWrapper,
    EmptyPlaylistWrapper,
    EmptyTextWrapper
} from './HighlightsPlaylist.style';

const getDefaultVideoIndex = (highlights, defaultVideoSlug) => {
    let defaultIndex = 0;
    for (let i = 0; i < highlights?.length; i++) {
        if (highlights[i]?.slug === defaultVideoSlug) {
            defaultIndex = i;
            break;
        }
    }

    return defaultIndex;
};

export const HighlightsPlaylist = ({
    autoPlay = false,
    captions = false,
    defaultVideoSlug,
    extendedDisplay = false,
    highlights,
    isLoading,
    mute,
    gamePk
}) => {
    const translate = useTranslate();

    const [currentHighlight, setCurrentHighlight] = useState(null);
    // keep now playing index decoupled from currentHighlight to not show when in clickToPlay state
    const [showNowPlaying, setShowNowPlaying] = useState(false);
    const [enableAutoPlay, setEnableAutoPlay] = useState(autoPlay);
    const [playerWrapper, setPlayerWrapper] = useState(null);
    const autoPlayTracking = useRef(autoPlay);

    const selectHighlight = useCallback((highlight) => {
        setCurrentHighlight(highlight);
        setShowNowPlaying(true);
        setEnableAutoPlay(true);
        autoPlayTracking.current = false;
    }, []);

    const handleMetaDataLoaded = useCallback((mediaItem) => {
        if (!mediaItem) {
            return;
        }
        setCurrentHighlight(mediaItem);
        setShowNowPlaying(true);
        setEnableAutoPlay(true);
    }, []);

    useEffect(() => {
        setEnableAutoPlay(autoPlay);
        autoPlayTracking.current = autoPlay;
    }, [autoPlay]);

    useEffect(() => {
        setCurrentHighlight(null);
    }, [gamePk]);

    useEffect(() => {
        if (!currentHighlight) {
            const defaultVideoIndex = getDefaultVideoIndex(highlights, defaultVideoSlug);
            setCurrentHighlight(highlights?.[defaultVideoIndex]);
        }
    }, [highlights, defaultVideoSlug]);

    useEffect(() => {
        if (!highlights || !playerWrapper) return;
        // update playlist when highlights change and maintain current mediaItem position
        playerWrapper.updatePlaylist(highlights, playerWrapper.config.contentId);
    }, [highlights, playerWrapper]);

    const handlers = useMemo(() => {
        return {
            mediaComplete: () => {
                if (!playerWrapper) {
                    return;
                }
                const { list, contentId } = playerWrapper.config;
                if (list[list.length - 1]?.id === contentId) {
                    // stop playback to keep the playlist from looping and display clickToPlay
                    playerWrapper.resetPlayback();
                }
            },
            mediaReplay: () => {
                autoPlayTracking.current = false;
            },
            showClickToPlay: () => {
                setShowNowPlaying(false);
            },
            onClickToPlay: () => {
                setShowNowPlaying(true);
            },
            embedPlayerStart: () => {
                setShowNowPlaying(true);
            },
            setDynamicTrackingObj: (data) => {
                const newData = setAutoPlayTrackingObj(data, autoPlayTracking?.current);
                if (data?.wire?.pEventKey === 'MEDIA_COMPLETE' && !autoPlayTracking.current) {
                    autoPlayTracking.current = true;
                }
                return newData;
            }
        };
    }, [autoPlayTracking, playerWrapper]);

    if (isLoading || !highlights) {
        return <HighlightsPlaylistSkeleton $extendedDisplay={extendedDisplay} />;
    }

    return (
        <HighlightPlaylistWrapper $extendedDisplay={extendedDisplay}>
            {highlights.length > 0
                ? (
                    <>
                        <HighlightVideoPlayer
                            handlers={handlers}
                            highlight={currentHighlight}
                            highlightList={highlights}
                            enableAutoPlay={enableAutoPlay}
                            onMetaDataLoaded={handleMetaDataLoaded}
                            extendedDisplay={extendedDisplay}
                            setPlayerWrapper={setPlayerWrapper}
                            autoPlayTracking={autoPlayTracking}
                            mute={mute}
                        />
                        <HighlightCardList
                            highlights={highlights}
                            nowPlayingIndex={showNowPlaying ? highlights?.findIndex(entry => entry.id === currentHighlight?.id) : undefined}
                            handleCardClick={selectHighlight}
                        />
                    </>
                )
                : (
                    <EmptyPlaylistWrapper>
                        <EmptyTextWrapper>{translate('video_empty_message', 'Sorry, no videos were found.')}</EmptyTextWrapper>
                    </EmptyPlaylistWrapper>
                )
            }
        </HighlightPlaylistWrapper>
    );
};

HighlightsPlaylist.propTypes = {
    autoPlay: PropTypes.bool,
    captions: PropTypes.bool,
    defaultVideoSlug: PropTypes.string,
    disabledVideoAutoPlay: PropTypes.bool,
    extendedDisplay: PropTypes.bool,
    highlights: PropTypes.array,
    isLoading: PropTypes.bool,
    mute: PropTypes.bool,
    gamePk: PropTypes.number
};

export default HighlightsPlaylist;
